import { useIntl } from 'react-intl';

import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Select } from '~/eds/src/components/Select/Select';
import { Nullable } from '~/types/Nullable';

import { TimePeriodCustomSettings } from '../types/fields';
import { WorkflowQuestionFormField } from './types';

export const TimePeriodWorkflowQuestion = ({
  question,
  onChange,
  ...commonProps
}: WorkflowQuestionFormField<Nullable<string>>) => {
  const { isPanelEnlarged } = useAppConfigContext();
  const intl = useIntl();
  const { label, description, id, ...rest } = commonProps;
  const {
    field: { value, customSettings },
  } = question;
  const { options } = customSettings as TimePeriodCustomSettings;

  const timePeriodLabel = intl.formatMessage(
    {
      id: 'TimePeriodWorkflowQuestion.timePeriodLabel',
      defaultMessage: '{questionTitle} - Time Period',
      description:
        'Time period question field in an intake form, the time period selection input. Example: questionTitle = "Contract Duration"',
    },
    { questionTitle: label },
  );
  const amountLabel = intl.formatMessage(
    {
      id: 'TimePeriodWorkflowQuestion.amountLabel',
      defaultMessage: '{questionTitle} - Amount',
      description:
        'Time period question field in an intake form, the amount of time input. Example: questionTitle = "Contract Duration"',
    },
    { questionTitle: label },
  );

  return (
    <Layout
      direction={isPanelEnlarged ? 'row' : 'column'}
      spacing={isPanelEnlarged ? 2 : 8}
    >
      <Box flex={isPanelEnlarged ? '0 0 auto' : undefined}>
        <FormField
          {...rest}
          id={`${id}-amount`}
          name={`${id}-amount`}
          label={amountLabel}
          description={description}
          input={NumberInput}
          value={value.amount}
          onChange={(val) => onChange(val, 'amount')}
          inputProps={{
            width: isPanelEnlarged ? 'input.m.width' : '100%',
          }}
        />
      </Box>
      <Box flex={isPanelEnlarged ? '0 0 auto' : undefined}>
        <FormField
          {...rest}
          id={`${id}-time-period`}
          name={`${id}-time-period`}
          label={timePeriodLabel}
          input={Select}
          value={value.period || null}
          onChange={(val) => onChange(val, 'period')}
          inputProps={{
            isMulti: false,
            enablePortal: true,
            options: options || [],
            width: isPanelEnlarged ? 'input.m.width' : '100%',
          }}
        />
      </Box>
    </Layout>
  );
};
